import { useEffect, useState } from 'react'
import { AnnotationsMenu } from './menus'
import { DEFAULT_STROKE_COLOR } from './constants'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { ColorPallete } from '@/components/icons/ColorPallete'
import AnnotationsToolbarMenuItem from './AnnotationsToolbarMenuItem'
import { AnnotationsColorOptions } from './AnnotationsColorOptions'

type AnnotationsToolbarMenuProps = {
  isDisabled: boolean
  menuConfig: AnnotationsMenu
}
export const AnnotationsToolbarMenu = ({
  menuConfig: {
    id: menuId,
    TriggerIcon,
    tooltipContent,
    colorOptionsEnabled,
    columns,
    menuItems,
    label,
  },
  isDisabled,
}: AnnotationsToolbarMenuProps) => {
  const [selectedColor, setSelectedColor] = useState(DEFAULT_STROKE_COLOR)
  const [showColorOptions, setShowColorOptions] = useState(false)

  useEffect(() => {
    setShowColorOptions(false)
  }, [selectedColor])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={`annotations-menu-${menuId}-button`}
        disabled={isDisabled}
        className={cn(
          'rounded-md hover:bg-gray-100 m-0 flex items-center justify-center h-full',
          {
            'opacity-25': isDisabled,
          },
        )}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className="rounded-md hover:bg-gray-100 m-0 h-full w-auto flex p-1 items-center justify-center min-w-[50px]"
                disabled={isDisabled}
              >
                <span className="flex items-center flex-col space-y-0.5 h-full justify-between">
                  <TriggerIcon className="h-6 w-full" />
                  {label && <span className="text-xs">{label}</span>}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-black text-white">
              <p>{tooltipContent}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="flex flex-col min-w-0 max-w-44 px-1.5 py-2">
        {showColorOptions && (
          <div
            className="absolute max-w-[80px] inset-x-0 mx-auto bg-white border shadow rounded-md"
            style={{ transform: 'translateY(-105%)' }}
          >
            <AnnotationsColorOptions
              selectedColor={selectedColor}
              setSelectedColor={setSelectedColor}
            />
          </div>
        )}
        {colorOptionsEnabled && (
          <div className="flex items-center justify-center mb-2">
            <span
              className="rounded-full m-1 text-center text-xs cursor-pointer"
              onClick={() => setShowColorOptions(!showColorOptions)}
            >
              <ColorPallete className="h-5 w-5" />
            </span>
          </div>
        )}
        <div
          className="grid min-w-0 max-w-44 gap-2"
          style={{
            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            overflow: 'visible',
          }}
        >
          {menuItems.map((menuItem) => (
            <DropdownMenuItem
              data-testid={`annotations-menu-${menuId}-option`}
              key={`${menuItem.id}${menuItem.size ? `-${menuItem.size}` : ''}`}
              className="w-full h-full flex items-center justify-center p-0"
            >
              <AnnotationsToolbarMenuItem
                strokeColor={selectedColor}
                {...menuItem}
              />
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
